import React from 'react';
import { Box, Typography } from '@mui/material';
import { DASHBOARD_COLORS } from '../styles';

/**
 * @typedef {Object} BatteryIndicatorProps
 * @property {number | string} percentage Battery percentage (0-100)
 * @property {boolean} [showPercentage] Whether to show percentage text (default: true)
 * @property {import('@mui/material').BoxProps} [boxProps] Additional styling
 */

/**
 * Reusable battery indicator component with dynamic fill
 * @param {BatteryIndicatorProps} props
 */
const BatteryIndicator = ({
  percentage,
  showPercentage = true,
  boxProps = {},
}) => {
  const isValid = typeof percentage === 'number' && !isNaN(percentage);
  const safePercentage = isValid ? Math.max(0, Math.min(100, percentage)) : 0;
  const displayValue = isValid ? `${safePercentage}%` : 'N/A';

  const getBatteryColor = (percentage) => {
    if (percentage >= 70) return DASHBOARD_COLORS.success;
    if (percentage > 20) return DASHBOARD_COLORS.warning;
    return DASHBOARD_COLORS.error;
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...boxProps }}>
      {showPercentage && (
        <Typography variant="body2">{displayValue}</Typography>
      )}
      <Box
        sx={{
          boxSizing: 'border-box',
          width: 20,
          height: 10,
          border: '1px solid',
          borderRadius: '3px',
          padding: '1px',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '2px',
            right: '-4px',
            width: '2px',
            height: '4px',
            borderRadius: '1px',
            backgroundColor: DASHBOARD_COLORS.mutedText,
          },
        }}
      >
        <Box
          sx={{
            boxSizing: 'border-box',
            height: '100%',
            width: `${safePercentage}%`,
            backgroundColor: getBatteryColor(safePercentage),
            borderRadius: '2px',
          }}
        />
      </Box>
    </Box>
  );
};

export default BatteryIndicator;
